import React from 'react';
import { Typography, List, ListItem, ListItemText, Divider } from '@mui/material';
import HelpSidebar from './HelpSidebar';
import AskAardvarkHelpHint from './AskAardvarkHelpHint';

interface SettingsHelpSidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export const SettingsHelpSidebar: React.FC<SettingsHelpSidebarProps> = ({
  isExpanded,
  onToggle,
}) => (
  <HelpSidebar
    isExpanded={isExpanded}
    onToggle={onToggle}
    title="Settings Help"
  >
    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
      This page configures system-wide settings: governance and compliance wording, email and check-in opt-out acknowledgments, date & locale, review cycle behaviour, and review policy. All changes are saved to the database and take effect immediately. After you click <strong>Save settings</strong>, a short confirmation appears at the bottom of the screen when the save succeeds.
    </Typography>

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      GOVERNANCE AND COMPLIANCE
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Post-login acknowledgment"
          secondary="When you set the Governance and Compliance text here, all users must read and acknowledge it in a modal after login before they can use the app. The wording is typically used for data protection (e.g. GDPR, POPIA, AI Act), secure use, data rights, and data retention. Acknowledgments are valid only for the current compliance period (calendar year) and the current text; if you change the text or a new year starts, users will be asked to acknowledge again. Users can view the current wording and when they last acknowledged on their Profile. Changes and user acknowledgments are recorded in the user audit log."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      OPT-OUT ACKNOWLEDGMENTS
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Email opt-out wording"
          secondary="Shown when someone turns off all optional emails. If you change this text, users who already opted out must reaffirm on next login (or turn emails back on). Changes are recorded in the user audit log."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Check-in opt-out wording"
          secondary="Points shown when a manager turns off built-in check-ins (one point per line). If you change this text, opted-out managers must reaffirm on next login (or turn built-in check-ins back on). Changes are recorded in the user audit log."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      REVIEW CYCLE CONFIGURATION
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Calendar vs fiscal year"
          secondary="Choose whether cycles align with calendar quarters (Q1: Jan–Mar, etc.) or fiscal year quarters. When fiscal year is used, set the start month and day (e.g. October 1)."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Auto-create and auto-start"
          secondary="Enable automatic creation of new cycles when the current cycle reaches the threshold (default 75%). Optionally auto-start new cycles so reviews are created immediately. Set the default employee cutoff (days before cycle end) for when employees must complete their part."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      COUNTRY & PUBLIC HOLIDAYS
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Country"
          secondary="System Settings country drives org-wide date-picker blocking (weekends/holidays), birthday/anniversary notification scheduling (last working day before when the event falls on a weekend/holiday), and the default time zone for Times on screen. Leave working-day calculations use each employee's company country from Reference Data. Supported countries: ZA, GB, IN. Indian employees observe the South Africa public holiday calendar. Public holiday tabs below (ZA, GB, IN) show stored dates from Nager.Date; the India tab displays ZA dates. People can choose their preferred time zone on Profile; leave days and holidays are always shown in the organisation time zone."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      PUBLIC HOLIDAYS (ADMIN)
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Tables and year filter"
          secondary="You see which calendar years have been synced and when (organisation time zone, or your preferred time zone on Profile), and the stored holiday dates and names. Sort columns and use pagination on the holiday list. Click a calendar year in the sync table to filter the holiday list to that year; click the same year again to clear the filter."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Sync from Nager now"
          secondary="Refreshes public holidays from Nager.Date for the selected country tab, for the usual year window (India tab refreshes the South Africa calendar). Use when the official calendar changed. Success and errors are shown next to the controls."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Recalculate all leave working days"
          secondary="Recomputes stored working-day totals on every leave booking from each request’s dates and public holidays per employee company country (same rules as normal saves). Run after refreshing holidays or changing company country if counts look wrong. Confirm in the dialog; results show how many rows updated or were unchanged. The same action exists on the Leave audit log. Changes are written to the leave audit trail."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      REVIEW POLICY SETTINGS
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Allow Peer Reviews"
          secondary="When enabled, employees can nominate peers to rate objectives and peers are invited on self-review submit; managers can add peer reviewers from the review. When disabled, only Team Leads and Managers can perform reviews. Set minimum and maximum peer reviewers when enabled."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>
    <AskAardvarkHelpHint about="public holidays on file" />
  </HelpSidebar>
);

export default SettingsHelpSidebar;
